/** Ressources déclarées : lecture inerte, indépendante des captures et de l'inventaire des écrans. */
import { resolveMeasuredFont } from '@/uptk/core/theme/module_fonts';
import type { ModuleSpecPresentation } from '../data/moduleSpecContracts';
import { normalizeHexInput } from '../presentation/presentationModel';
import { ICON_ALIASES, ICON_KEYWORDS } from '../catalog/navIconVocabulary';
import { luminanceOf } from '@/uptk/core/theme/module_theme';

export type ColorRole = 'background' | 'surface' | 'text' | 'accent' | 'state' | 'border';
export interface SourceVariable { name: string; value: string; scope: string; color: string | null; role: ColorRole }
export interface SourceFont { familyStack: string; role: 'body' | 'heading' | 'mono'; status: 'embedded' | 'web' | 'system'; sources: string[]; pieceId?: string }
export interface SourceImage { url: string; name: string; kind: 'image' | 'background' | 'favicon' | 'svg'; occurrences: number; icon: string | null; pieceId?: string; width?: number; height?: number; bytes?: number }
export interface SourceResources { variables: SourceVariable[]; fonts: SourceFont[]; images: SourceImage[]; externalStyles: string[] }

/** Découpe CSS en respectant chaînes, fonctions et blocs imbriqués (les data: peuvent contenir des points-virgules). */
function splitCss(text: string, delimiter: string): string[] {
  const parts: string[] = [];
  let start = 0, depth = 0, quote = '';
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (c === '\\') { i += 1; continue; }
    if (quote) { if (c === quote) quote = ''; continue; }
    if (c === '"' || c === "'") { quote = c; continue; }
    if (c === '(' || c === '[') depth += 1;
    if (c === ')' || c === ']') depth -= 1;
    if (c === delimiter && depth === 0) { parts.push(text.slice(start, i)); start = i + 1; }
  }
  parts.push(text.slice(start));
  return parts;
}

function rules(text: string, parent = ''): { selector: string; scope: string; declarations: Map<string, string> }[] {
  const out: ReturnType<typeof rules> = [];
  const css = text.replace(/\/\*[\s\S]*?\*\//g, '');
  let start = 0, open = -1, depth = 0, quote = '';
  for (let i = 0; i < css.length; i += 1) {
    const c = css[i];
    if (c === '\\') { i += 1; continue; }
    if (quote) { if (c === quote) quote = ''; continue; }
    if (c === '"' || c === "'") { quote = c; continue; }
    if (c === '{') { if (depth === 0) open = i; depth += 1; }
    if (c !== '}') continue;
    depth -= 1;
    if (depth !== 0) continue;
    const selector = css.slice(start, open).trim();
    const body = css.slice(open + 1, i);
    if (/^@(media|supports|layer|container)\b/i.test(selector)) out.push(...rules(body, [parent, selector].filter(Boolean).join(' / ')));
    else {
      const declarations = new Map<string, string>();
      for (const declaration of splitCss(body, ';')) {
        const colon = declaration.indexOf(':');
        if (colon > 0) declarations.set(declaration.slice(0, colon).trim(), declaration.slice(colon + 1).trim().replace(/\s*!important\s*$/i, ''));
      }
      out.push({ selector, scope: [parent, selector].filter(Boolean).join(' / '), declarations });
    }
    start = i + 1;
  }
  return out;
}

export function cssUrls(value: string): string[] {
  return Array.from(value.matchAll(/url\(\s*(?:"([^"]*)"|'([^']*)'|([^)]*?))\s*\)/gi), m => (m[1] ?? m[2] ?? m[3]).trim());
}

export function colorRole(name: string): ColorRole {
  if (/(?:good|bad|success|error|danger|warning|info|valid|invalid|positive|negative)/i.test(name)) return 'state';
  if (/(?:bg|background)/i.test(name)) return 'background';
  if (/(?:surface|panel|card)/i.test(name)) return 'surface';
  if (/(?:text|ink|muted|foreground)/i.test(name)) return 'text';
  if (/(?:line|border|divider)/i.test(name)) return 'border';
  return 'accent';
}

const SVG_TAGS = new Set(['svg', 'g', 'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'title', 'desc', 'defs', 'linearGradient', 'radialGradient', 'stop', 'clipPath', 'use']);
const SVG_ATTRS = new Set(['xmlns', 'viewBox', 'width', 'height', 'x', 'y', 'x1', 'x2', 'y1', 'y2', 'cx', 'cy', 'r', 'rx', 'ry', 'd', 'points', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'fill-rule', 'clip-rule', 'opacity', 'fill-opacity', 'stroke-opacity', 'transform', 'id', 'offset', 'stop-color', 'stop-opacity', 'gradientUnits', 'gradientTransform', 'clip-path', 'href']);

/** Reconstruction par liste blanche ; aucun script, style, animation, lien externe ni événement. */
export function sanitizeSvg(element: Element): string {
  const clean = (node: Element): Element | null => {
    if (!SVG_TAGS.has(node.localName)) return null;
    const result = node.ownerDocument.createElementNS('http://www.w3.org/2000/svg', node.localName);
    for (const attr of Array.from(node.attributes)) {
      if (!SVG_ATTRS.has(attr.name)) continue;
      if (/(?:url\(|javascript:|data:|https?:|\/\/)/i.test(attr.value) && !/^url\(#[\w-]+\)$/.test(attr.value)) continue;
      if (attr.name === 'href' && !/^#[\w-]+$/.test(attr.value)) continue;
      result.setAttribute(attr.name, attr.value);
    }
    if (node.localName === 'title' || node.localName === 'desc') result.textContent = node.textContent;
    else for (const child of Array.from(node.children)) { const next = clean(child); if (next) result.appendChild(next); }
    return result;
  };
  const svg = clean(element);
  if (!svg) return '';
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  return new XMLSerializer().serializeToString(svg);
}

function iconMatch(name: string): string | null {
  const token = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/^(?:icon-|lucide-|fa-)/, '');
  if (Object.prototype.hasOwnProperty.call(ICON_ALIASES, token)) return ICON_ALIASES[token];
  const words = token.split(/[^a-z0-9]+/);
  return ICON_KEYWORDS.find(row => row.words.some(keyword => words.some(word => keyword.length >= 4 ? word.startsWith(keyword) : word === keyword)))?.icon ?? null;
}

export function readSourceResources(html: string): SourceResources {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const cssRules = rules(Array.from(doc.querySelectorAll('style'), node => node.textContent ?? '').join('\n'));
  for (const node of Array.from(doc.querySelectorAll('[style]'))) cssRules.push({ selector: node.localName, scope: 'inline', declarations: new Map(splitCss(node.getAttribute('style') ?? '', ';').map(part => { const i = part.indexOf(':'); return [part.slice(0, i).trim(), part.slice(i + 1).trim()]; })) });
  const variables: SourceVariable[] = [];
  const fonts: SourceFont[] = [];
  const images = new Map<string, SourceImage>();
  const faces = new Map<string, string[]>();
  const addImage = (url: string, name: string, kind: SourceImage['kind'], icon: string | null = null) => {
    if (!url || url.startsWith('#') || images.size >= 200) return;
    const existing = images.get(url);
    if (existing) existing.occurrences += 1;
    else images.set(url, { url, name, kind, occurrences: 1, icon });
  };
  for (const rule of cssRules) {
    if (rule.selector === '@font-face') { faces.set((rule.declarations.get('font-family') ?? '').replace(/["']/g, '').toLowerCase(), cssUrls(rule.declarations.get('src') ?? '')); continue; }
    for (const [name, value] of rule.declarations) {
      if (name.startsWith('--') && /:root|\[data-theme\s*=/.test(rule.selector)) variables.push({ name, value, scope: rule.scope, color: normalizeHexInput(value), role: colorRole(name) });
      if (name.startsWith('--') || /^(background|background-image|mask|mask-image|content|list-style-image)$/.test(name)) for (const url of cssUrls(value)) addImage(url, name.startsWith('--') ? name : rule.selector, 'background');
    }
  }
  const google = new Set<string>();
  const externalStyles: string[] = [];
  for (const link of Array.from(doc.querySelectorAll('link[href]'))) {
    const href = link.getAttribute('href') ?? '';
    if ((link.getAttribute('rel') ?? '').split(/\s+/).some(rel => rel === 'icon' || rel === 'apple-touch-icon')) addImage(href, link.getAttribute('title') ?? 'favicon', 'favicon');
    if (link.getAttribute('rel') !== 'stylesheet') continue;
    try {
      const url = new URL(href);
      if (url.origin === 'https://fonts.googleapis.com' && /^\/css2?$/.test(url.pathname)) {
        for (const family of url.searchParams.getAll('family').flatMap(value => value.split('|'))) google.add(family.split(':')[0].toLowerCase());
      } else externalStyles.push(href);
    } catch { externalStyles.push(href); }
  }
  for (const rule of cssRules) {
    let stack = rule.declarations.get('font-family') ?? rule.declarations.get('font')?.match(/(?:\d*\.)?\d+(?:px|rem|em|pt|%)\s*(?:\/\s*[^\s]+\s+|\s+)(.+)$/i)?.[1];
    if (!stack || rule.selector === '@font-face') continue;
    for (let depth = 0; depth < 8 && stack.includes('var('); depth += 1) stack = stack.replace(/var\((--[\w-]+)(?:,\s*([^)]*))?\)/g, (_, name: string, fallback: string) => variables.find(variable => variable.name === name && variable.scope === ':root')?.value ?? fallback ?? '');
    const role: SourceFont['role'] = /(?:\bh[1-6]\b|title|heading)/i.test(rule.selector) ? 'heading' : /(?:\bpre\b|\bcode\b|mono)/i.test(rule.selector) ? 'mono' : 'body';
    const family = splitCss(stack, ',')[0].replace(/["']/g, '').trim().toLowerCase();
    if (!fonts.some(font => font.familyStack === stack && font.role === role)) fonts.push({ familyStack: stack, role, status: faces.has(family) ? (faces.get(family)!.some(url => url.startsWith('data:')) ? 'embedded' : 'web') : google.has(family) ? 'web' : 'system', sources: faces.get(family) ?? [] });
  }
  for (const [family, sources] of faces) if (!fonts.some(font => font.sources === sources)) fonts.push({ familyStack: family, role: 'body', status: sources.some(url => url.startsWith('data:')) ? 'embedded' : 'web', sources });
  for (const family of google) if (!fonts.some(font => font.familyStack.toLowerCase().includes(family))) fonts.push({ familyStack: family, role: 'body', status: 'web', sources: [] });
  for (const node of Array.from(doc.querySelectorAll('img, source[srcset]'))) {
    if (node.hasAttribute('src')) addImage(node.getAttribute('src') ?? '', node.getAttribute('alt') ?? '', 'image');
    // Une data: garde sa virgule interne ; les descripteurs séparent les candidats.
    const srcset = node.getAttribute('srcset') ?? '';
    for (const match of srcset.matchAll(/(?:^|,\s*)(data:[^\s]+|[^\s,]+)(?:\s+\d+(?:\.\d+)?[wx])?/g)) addImage(match[1].replace(/,$/, ''), node.getAttribute('alt') ?? '', 'image');
  }
  for (const svg of Array.from(doc.querySelectorAll('svg'))) {
    const name = svg.getAttribute('aria-label') ?? svg.querySelector('title')?.textContent ?? '';
    const sanitized = sanitizeSvg(svg);
    const url = `data:image/svg+xml,${encodeURIComponent(sanitized)}`;
    addImage(url, name, 'svg', iconMatch(name));
    const image = images.get(url);
    const box = (svg.getAttribute('viewBox') ?? '').trim().split(/[\s,]+/);
    if (image) {
      image.width = Math.ceil(Number.parseFloat(svg.getAttribute('width') ?? box[2] ?? '24'));
      image.height = Math.ceil(Number.parseFloat(svg.getAttribute('height') ?? box[3] ?? '24'));
      image.bytes = new Blob([sanitized]).size;
    }
  }
  return { variables, fonts, images: [...images.values()], externalStyles };
}

/** Complète le contrat TYPO 1.6 existant ; ne produit aucune taille typographique inventée. */
export function presentationFromSource(resources: SourceResources, current: ModuleSpecPresentation | null): ModuleSpecPresentation {
  const accents = resources.variables.filter(variable => variable.role === 'accent' && variable.color && variable.scope === ':root');
  const chosenAccent = accents.find(variable => /^--(?:brand|accent|primary|gold)$/.test(variable.name)) ?? accents[0];
  const primary = chosenAccent?.color ?? current?.palette?.accent;
  const next: ModuleSpecPresentation = { ...current, origin: 'manual', ...(primary ? { palette: { accent: primary, ...current?.palette } } : {}), typography: { ...current?.typography } };
  for (const variable of resources.variables) {
    if (!variable.color || variable.role === 'state' || variable.scope !== ':root') continue;
    const slot = variable.role === 'background' ? 'surface' : variable.role === 'surface' ? 'surfaceRaised' : variable.role === 'border' ? 'border' : variable.role === 'text' ? /muted/i.test(variable.name) ? 'inkMuted' : 'ink' : 'accent';
    // Le premier accent déclaré est l'accent principal ; les autres restent visibles dans le relevé.
    if (slot === 'accent' && variable !== chosenAccent) continue;
    if (next.palette) next.palette[slot] = variable.color;
  }
  for (const font of resources.fonts) {
    const family = resolveMeasuredFont(splitCss(font.familyStack, ',')[0]);
    if (family.fallback) continue;
    if (font.role === 'body' || font.role === 'heading') next.typography![font.role] = { family: family.key, weights: [400, 700] };
  }
  delete next.extractor;
  const background = resources.variables.find(variable => variable.role === 'background' && variable.color && variable.scope === ':root');
  if (background?.color) next.baseScheme = luminanceOf(background.color) < 0.5 ? 'dark' : 'light';
  return next;
}
